import React, { createContext, useContext, useReducer, useCallback } from 'react';

const CartContext = createContext();

const ACTIONS = {
  ADD: 'ADD',
  REMOVE: 'REMOVE',
  UPDATE_QTY: 'UPDATE_QTY',
  CLEAR: 'CLEAR',
  TOGGLE_DRAWER: 'TOGGLE_DRAWER',
};

function cartReducer(state, action) {
  switch (action.type) {
    case ACTIONS.ADD: {
      const existing = state.items.find((i) => i.id === action.payload.id);
      if (existing) {
        return {
          ...state,
          items: state.items.map((i) =>
            i.id === action.payload.id ? { ...i, qty: i.qty + 1 } : i
          ),
          drawerOpen: true,
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, qty: 1 }],
        drawerOpen: true,
      };
    }
    case ACTIONS.REMOVE:
      return {
        ...state,
        items: state.items.filter((i) => i.id !== action.payload),
      };
    case ACTIONS.UPDATE_QTY:
      if (action.payload.qty <= 0) {
        return {
          ...state,
          items: state.items.filter((i) => i.id !== action.payload.id),
        };
      }
      return {
        ...state,
        items: state.items.map((i) =>
          i.id === action.payload.id ? { ...i, qty: action.payload.qty } : i
        ),
      };
    case ACTIONS.CLEAR:
      return { ...state, items: [], drawerOpen: false };
    case ACTIONS.TOGGLE_DRAWER:
      return { ...state, drawerOpen: action.payload ?? !state.drawerOpen };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    drawerOpen: false,
  });

  const addItem = useCallback((product) => {
    dispatch({ type: ACTIONS.ADD, payload: product });
  }, []);

  const removeItem = useCallback((id) => {
    dispatch({ type: ACTIONS.REMOVE, payload: id });
  }, []);

  const updateQty = useCallback((id, qty) => {
    dispatch({ type: ACTIONS.UPDATE_QTY, payload: { id, qty } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR });
  }, []);

  const toggleDrawer = useCallback((open) => {
    dispatch({ type: ACTIONS.TOGGLE_DRAWER, payload: open });
  }, []);

  const totalItems = state.items.reduce((sum, i) => sum + i.qty, 0);
  const totalPrice = state.items.reduce((sum, i) => sum + (i.price || 0) * i.qty, 0);
  const hasCustomItems = state.items.some((i) => !i.price);

  return (
    <CartContext.Provider
      value={{
        items: state.items,
        drawerOpen: state.drawerOpen,
        totalItems,
        totalPrice,
        hasCustomItems,
        addItem,
        removeItem,
        updateQty,
        clearCart,
        toggleDrawer,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
